<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessSteps from "~/components/molecules/WrDesignProcessSteps.vue";
import WrDesignWhyItMattersSection from "~/components/molecules/WrDesignWhyItMattersSection.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrDesignScenarioCards from "~/components/molecules/WrDesignScenarioCards.vue";
import WrDesignStrugglesSection from "~/components/molecules/WrDesignStrugglesSection.vue";
import WrDesignComparisonSection from "~/components/molecules/WrDesignComparisonSection.vue";
import WrDesignWhyChooseUsCard from "~/components/molecules/WrDesignWhyChooseUsCard.vue";
import WrDesignPrePlanningSection from "~/components/molecules/WrDesignPrePlanningSection.vue";
import WrDesignIconLineDecorativeSquaresCards from "~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue";
import WrDesignLevelUpYourCreativityWithAi from "~/components/molecules/WrDesignLevelUpYourCreativityWithAi.vue";
import WrDesignReasonsSection from "~/components/molecules/WrDesignReasonsSection.vue";
import WrDesignQuoteCtaSection from "~/components/molecules/WrDesignQuoteCtaSection.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrIcon from "~/components/global/WrIcon.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrTestimonialCardCarousel from "~/components/molecules/WrTestimonialCardCarousel.vue";

const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};


/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/solutions/field-service-management-software-fsm-development-services-content.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  processMotion,
  techStack,
  stackIntegration,
  integrationCards: integrationCardsData,
  useCases,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Solutions", item: "https://webreinvent.com/solutions" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessSteps: { id, svgIcon, title, description }
const quickHitCards = computed(() =>
  quickHits.items.map((item: any, i: number) => ({
    id: String(i + 1),
    svgIcon: item.svgIcon,
    title: item.title,
    description: item.description,
  }))
);

// WhyItMattersSection: { svgIcon, title, description }
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// ScenarioCards: { title, problem, solution, result }
const capabilityCards = computed(() =>
  capabilities.items.map((card: any) => ({
    title: card.title,
    problem: card.problem,
    solution: card.solution,
    result: card.result,
  }))
);

// StrugglesSection: { label, svgIcon }
const struggleItems = computed(() =>
  processMotion.struggles.map((item: any) => ({
    label: item.label,
    svgIcon: item.svgIcon,
  }))
);

// ComparisonSection: left & right groups
const comparisonLeftCards = computed(() =>
  techStack.groupOneCards.map((item: any) => ({
    svgIcon: item.svgIcon,
    title: item.title,
    description: item.description,
  }))
);
const comparisonRightCards = computed(() =>
  techStack.groupTwoCards.map((item: any) => ({
    svgIcon: item.svgIcon,
    title: item.title,
    description: item.description,
  }))
);

// UseCases: IconLineDecorativeSquaresCards — { number, title, description }
const useCaseCards = computed(() =>
  useCases.items.map((item: any, i: number) => ({
    number: String(i + 1),
    title: item.title,
    description: item.description,
  }))
);
const useCaseSvgIcons = computed(() =>
  useCases.items.map((item: any) => item.svgIcon)
);

// WhyChooseUs: LevelUpYourCreativityWithAi — { svgIcon, title, description }
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((item: any) => ({
    svgIcon: item.svgIcon,
    title: item.title,
    description: item.description,
  }))
);

// Integration cards: ReasonsSection — cards: { title, desc }, svgIcons: string[]
const integrationCardItems = computed(() =>
  integrationCardsData.items.map((item: any) => ({
    title: item.title,
    desc: item.description,
  }))
);
const integrationSvgIcons = computed(() =>
  integrationCardsData.items.map((item: any) => item.svgIcon)
);

const faqItems = computed(() => faq.faqItems);

// Clean hero alt text by stripping HTML tags (e.g. <br />)
const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card (shared) -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text (shared) -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Quick Hits (ProcessSteps - 4 step cards with corner squares) — unique -->
      <section aria-label="FSM Capabilities">
        <WrDesignProcessSteps
          :heading="quickHits.heading"
          :subcopy="quickHits.subcopy"
          :steps="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 3: Business Outcomes (WhyItMattersSection - 4 horizontal cards) — unique -->
      <section aria-label="Business Outcomes">
        <WrDesignWhyItMattersSection
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
          :cta-text="businessOutcomes.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 4: Problem / Solution (ProblemSolutionSection) — shared -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (ScenarioCards - 3 problem/solution/result) — unique -->
      <section aria-label="FSM Challenges and Solutions">
        <WrDesignScenarioCards
          :heading="capabilities.heading"
          :subcopy="capabilities.subcopy"
          :cards="capabilityCards"
        />
      </section>

      <!-- Section 6: Pain Points (StrugglesSection - 7 scattered cards) — unique -->
      <section aria-label="Field Service Pain Points">
        <WrDesignStrugglesSection
          :heading="processMotion.heading"
          :subcopy="processMotion.subcopy"
          :struggles="struggleItems"
          :cta-text="processMotion.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(item, idx) in struggleItems" :key="item.label" #[`icon-${idx}`]>
            <WrIcon :name="item.svgIcon" class="size-4 lg:size-6 text-black dark:text-white" />
          </template>
        </WrDesignStrugglesSection>
      </section>

      <!-- Section 7: AI with Humans (ComparisonSection - side-by-side) — unique -->
      <section aria-label="FSM Automation with Human Experts">
        <WrDesignComparisonSection
          :heading="techStack.heading"
          :subtitle="techStack.subtitle"
          :left-title="techStack.groupOneTitle"
          :left-cards="comparisonLeftCards"
          :right-title="techStack.groupTwoTitle"
          :right-cards="comparisonRightCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 8: Integration Banner (WhyChooseUsCard) — shared -->
      <section aria-label="FSM Integrations">
        <WrDesignWhyChooseUsCard
          :heading="stackIntegration.heading"
          :subcopy="stackIntegration.subcopy"
          :cta-text="stackIntegration.ctaText"
          :image-dark-src="stackIntegration.imageDarkSrc"
          :image-light-src="stackIntegration.imageLightSrc"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 9: Integration Details (ReasonsSection - 4 cards with icons) — unique -->
      <section aria-label="Integration Overview">
        <WrDesignReasonsSection
          :heading="integrationCardsData.heading"
          :subcopy="integrationCardsData.subcopy"
          :cards="integrationCardItems"
          :svg-icons="integrationSvgIcons"
          :cta-text="integrationCardsData.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Use Cases (IconLineDecorativeSquaresCards - 8 cards with icons) — unique -->
      <section aria-label="FSM Across Industries">
        <WrDesignIconLineDecorativeSquaresCards
          :heading="useCases.heading"
          :subcopy="useCases.subcopy"
          :cards="useCaseCards"
          :cta-text="useCases.ctaText"
          @cta-clicked="openModal"
        >
          <template #icon-0>
            <WrIcon :name="useCaseSvgIcons[0]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-1>
            <WrIcon :name="useCaseSvgIcons[1]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-2>
            <WrIcon :name="useCaseSvgIcons[2]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-3>
            <WrIcon :name="useCaseSvgIcons[3]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-4>
            <WrIcon :name="useCaseSvgIcons[4]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-5>
            <WrIcon :name="useCaseSvgIcons[5]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-6>
            <WrIcon :name="useCaseSvgIcons[6]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
          <template #icon-7>
            <WrIcon :name="useCaseSvgIcons[7]" class="size-6 sm:size-8 text-[#501D71] dark:text-white" />
          </template>
        </WrDesignIconLineDecorativeSquaresCards>
      </section>

      <!-- Section 11: Why Choose Us (LevelUpYourCreativityWithAi - 4 cards with icons) — unique -->
      <section aria-label="Why Choose WebReinvent for FSM Development">
        <WrDesignLevelUpYourCreativityWithAi
          :heading="whyChooseUs.heading"
          :subcopy="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 12: CTA (QuoteCtaSection - quote + CTA button) — unique -->
      <section aria-label="Call to Action">
        <WrDesignQuoteCtaSection
          :text="cta.subcopy"
          :cta-text="cta.ctaText"
          @cta-clicked="openModal"
        />
      </section>
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <WrDesignStatsCard />
      </section>

      <!-- Section 13: Testimonials -->
      <section class="relative mt-16 overflow-hidden max-w-screen-xl mx-auto px-4 z-20">
        <h2 class="text-center mb-11">
          <WrGradientText text="Trusted for excellence and efficiency" />
          <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
            WebReinvent is trusted by leading organisations to enhance their
            development and operational efficiency.
          </p>
        </h2>
        <WrTestimonialCardCarousel />
        <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
          View All
        </WrButton>
      </section>

      <!-- Section 14: FAQ (shared) -->
      <section aria-label="Frequently asked questions">
        <WrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>
          


    </div>
  </main>

  <WrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
